import '../../styles/pages/common/commonPage.scss';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import { plusSignSVG, scheduleSVG } from '../../lib/allCustomSVGs';
import { useTranslation } from 'react-i18next';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import { useEffect, useState } from 'react';
import Popup from '../../ui/popup/Popup';
import get_prompt_schedules_api_call from '../../lib/api/prompt-schedules/get-prompt-schedules';
import update_prompt_schedule_api_call from '../../lib/api/prompt-schedules/update-prompt-schedule';
import delete_prompt_schedule_api_call from '../../lib/api/prompt-schedules/delete-prompt-schedule';
import MCPStatusCard from '../integrations/MCP/MCPStatusCard';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../ui/noData/NoData';
import makeFirstLetterCapital from '../../lib/helper-functions/makeFirstLetterCapital';
import store from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import FilterBox from '../../ui/boxes/FilterBox';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import PromptScheduleFormModal from './PromptScheduleFormModal';
import ViewPromptScheduleModal from './ViewPromptScheduleModal';
import type { PromptSchedule } from '../../lib/api/prompt-schedules/types';

function compareScheduleNames(a: { name?: string }, b: { name?: string }) {
  return (a.name ?? '').localeCompare(b.name ?? '', undefined, {
    sensitivity: 'base',
  });
}

const PromptSchedules = () => {
  const { t } = useTranslation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewingSchedule, setViewingSchedule] = useState<PromptSchedule | null>(
    null
  );
  const [editingSchedule, setEditingSchedule] = useState<PromptSchedule | null>(
    null
  );
  const [schedules, setSchedules] = useState<PromptSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilter, setSelectedFilter] = useState(() =>
    t('PromptSchedulesFilterAll')
  );

  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const list = await get_prompt_schedules_api_call();
      setSchedules(Array.isArray(list) ? list : []);
    } catch {
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const filtered = schedules
    .filter((s) => {
      const matchesSearch = !isNotEmptyString(searchValue)
        ? true
        : (s.name ?? '').toLowerCase().includes(searchValue.toLowerCase());
      if (!matchesSearch) return false;
      if (selectedFilter === t('PromptSchedulesFilterActive'))
        return s.is_active === true;
      if (selectedFilter === t('PromptSchedulesFilterInactive'))
        return s.is_active === false;
      if (selectedFilter === t('PromptScheduleTypeDaily'))
        return s.schedule_type === 'daily';
      if (selectedFilter === t('PromptScheduleTypeWeekly'))
        return s.schedule_type === 'weekly';
      if (selectedFilter === t('PromptScheduleTypeMonthly'))
        return s.schedule_type === 'monthly';
      return true;
    })
    .sort(compareScheduleNames);

  const handleToggle = async (schedule: PromptSchedule, newStatus: boolean) => {
    if (togglingId) return;
    setTogglingId(schedule.id);
    try {
      const res = await update_prompt_schedule_api_call(schedule.id, {
        is_active: newStatus,
      });
      if (res?.success) {
        setSchedules((prev) =>
          prev.map((x) =>
            x.id === schedule.id
              ? res.data
                ? { ...x, ...res.data }
                : { ...x, is_active: newStatus }
              : x
          )
        );
        store.dispatch(
          setMessage({
            message: t('PromptScheduleStatusUpdated'),
            messageType: 'success',
          })
        );
      } else {
        store.dispatch(
          setMessage({
            message: res?.message || t('SomethingWentWrong-trying-again-msg'),
            messageType: 'error',
          })
        );
      }
    } catch {
      store.dispatch(
        setMessage({
          message: t('SomethingWentWrong-trying-again-msg'),
          messageType: 'error',
        })
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (schedule: PromptSchedule) => {
    if (deletingId) return;
    setDeletingId(schedule.id);
    try {
      const res = await delete_prompt_schedule_api_call(schedule.id);
      if (res?.success) {
        setSchedules((prev) => prev.filter((x) => x.id !== schedule.id));
        if (viewingSchedule?.id === schedule.id) setViewingSchedule(null);
        if (editingSchedule?.id === schedule.id) setEditingSchedule(null);
        store.dispatch(
          setMessage({
            message: res?.message || t('PromptScheduleDeletedSuccess'),
            messageType: 'success',
          })
        );
      } else {
        store.dispatch(
          setMessage({
            message: res?.message || t('SomethingWentWrong-trying-again-msg'),
            messageType: 'error',
          })
        );
      }
    } catch {
      store.dispatch(
        setMessage({
          message: t('SomethingWentWrong-trying-again-msg'),
          messageType: 'error',
        })
      );
    } finally {
      setDeletingId(null);
    }
  };

  const openCreate = () => setIsCreateModalOpen(true);
  const closeCreate = () => setIsCreateModalOpen(false);
  const onCreated = () => {
    closeCreate();
    fetchSchedules();
  };

  const openEditFromView = () => {
    if (viewingSchedule) {
      setEditingSchedule(viewingSchedule);
      setViewingSchedule(null);
    }
  };

  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={t('PromptSchedulesHub')}
            backgroundColor={'var(--main-background-color)'}
            secondaryBtnTitle={t('CreatePromptSchedule')}
            secondaryBtnIcon={plusSignSVG}
            secondaryBtnLoading={false}
            secondaryBtnAction={openCreate}
            secondaryButtonType='white'
          >
            <div className='mcp_page'>
              <FilterBox
                filterOptions={[
                  t('PromptSchedulesFilterAll'),
                  t('PromptSchedulesFilterActive'),
                  t('PromptSchedulesFilterInactive'),
                  t('PromptScheduleTypeDaily'),
                  t('PromptScheduleTypeWeekly'),
                  t('PromptScheduleTypeMonthly'),
                ]}
                extraClassName='mcpIntegration-filterBox--identifier'
                initialFilter={t('PromptSchedulesFilterAll')}
                placeholder={t('SearchPromptSchedules')}
                onSearchChange={(value) => setSearchValue(value)}
                onFilterChange={(filter) => setSelectedFilter(filter)}
              />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2rem',
                  marginBottom: '2rem',
                }}
              >
                <div className='integrationContainer'>
                  {loading ? (
                    <LoadingSpinner />
                  ) : schedules.length > 0 ? (
                    filtered.length > 0 ? (
                      filtered.map((schedule) => (
                        <MCPStatusCard
                          key={schedule.id}
                          leftIcon={scheduleSVG}
                          info={makeFirstLetterCapital(schedule.name ?? '')}
                          active={schedule.is_active ? 'true' : 'false'}
                          customPillLabel={makeFirstLetterCapital(
                            schedule.schedule_type
                          )}
                          customPillColor={'grey'}
                          hideConfigure={true}
                          disabled={togglingId !== null || deletingId !== null}
                          onCardClick={() => setViewingSchedule(schedule)}
                          onStatusToggle={(newStatus) =>
                            handleToggle(schedule, newStatus)
                          }
                        />
                      ))
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          flexBasis: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '2rem 0',
                        }}
                      >
                        <NoData
                          title={t('NoPromptSchedulesSearchNoResults')}
                          subTitle={t('TryDifferentSearchOrFilter')}
                        />
                      </div>
                    )
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        flexBasis: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem 0',
                      }}
                    >
                      <NoData
                        title={t('NoPromptSchedulesFound')}
                        subTitle={t('CreatePromptScheduleToGetStarted')}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Popup
              styles={{ backgroundColor: '#fff' }}
              setTrigger={closeCreate}
              trigger={isCreateModalOpen}
            >
              <PromptScheduleFormModal
                mode='create'
                onClose={closeCreate}
                onSuccess={onCreated}
              />
            </Popup>

            <Popup
              styles={{ backgroundColor: '#fff' }}
              setTrigger={() => setEditingSchedule(null)}
              trigger={!!editingSchedule}
            >
              {editingSchedule && (
                <PromptScheduleFormModal
                  mode='edit'
                  schedule={editingSchedule}
                  onClose={() => setEditingSchedule(null)}
                  onSuccess={() => {
                    setEditingSchedule(null);
                    fetchSchedules();
                  }}
                />
              )}
            </Popup>

            <Popup
              styles={{ backgroundColor: '#fff' }}
              setTrigger={() => setViewingSchedule(null)}
              trigger={!!viewingSchedule}
            >
              {viewingSchedule && (
                <ViewPromptScheduleModal
                  schedule={viewingSchedule}
                  onEdit={openEditFromView}
                  onDelete={() => handleDelete(viewingSchedule)}
                  deleteDisabled={deletingId === viewingSchedule.id}
                />
              )}
            </Popup>
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>
    </PageWrapperMain>
  );
};

export default PromptSchedules;
